import { useCallback, useEffect, useRef, useState } from 'react';

export type FloatingPos = { top: number; left: number; width: number; direction: 'down' | 'up' };

const DEFAULT_DROPDOWN_HEIGHT_PX = 360;
const DEFAULT_DROPDOWN_WIDTH_PX = 280;
const VIEWPORT_EDGE_PADDING_PX = 8;

/**
 * Returns refs + computed position for a popover that opens below its anchor,
 * flipping above when there isn't enough room.
 */
export function useFloating(
  open: boolean,
  dropdownHeight: number = DEFAULT_DROPDOWN_HEIGHT_PX,
  dropdownWidth: number = DEFAULT_DROPDOWN_WIDTH_PX,
) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<FloatingPos>({ top: 0, left: 0, width: 0, direction: 'down' });

  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const goUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    const panelW = Math.max(dropdownWidth, rect.width);
    let left = rect.left + window.scrollX;
    const rightOverflow = rect.left + panelW - window.innerWidth;
    if (rightOverflow > 0) {
      left = Math.max(VIEWPORT_EDGE_PADDING_PX, left - rightOverflow - VIEWPORT_EDGE_PADDING_PX);
    }

    setPos({
      top: goUp ? rect.top + window.scrollY : rect.bottom + window.scrollY,
      left,
      width: rect.width,
      direction: goUp ? 'up' : 'down',
    });
  }, [dropdownHeight, dropdownWidth]);

  useEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  return { anchorRef, floatingRef, pos, reposition };
}
